/**
 * Port-time estimate for one automated route stop (Feedback #53, T9b).
 *
 * Mirrors what RouteAutomationSystem._onShipArrived really does at a stop, but
 * against a throw-away simulation (`sim`) instead of live state: contract cargo
 * for this port is unloaded first (ContractSystem), then each action runs in
 * order, actions the manager tier cannot perform are skipped, and the port time
 * is `max(cargo handling, bunkering)`. Cargo handling is ONE session per stop
 * (the fixed setup time is charged once, on the total TEU moved), exactly like
 * Ship.addHandling; bunkering is its own session that runs alongside it.
 *
 * Pure: no EventBus, no GameState. Server-importable. All context comes in via
 * `opts`. Price-dependent decisions can't be predicted; they are assumed to go
 * through and reported in `flags` so the UI can say so.
 */

const ASSUMED_TYPE = '__assumed__';   // lot bought by a smart/interest action: cargo type unknown

function isFill(q) { return q === 'toFull' || q === 'all'; }
function lotTeu(lots) { return lots.reduce((s, l) => s + (l.quantity || 0), 0); }

/**
 * @param {Ship}   ship     only getCargoCapacity / estimateHandlingHours / estimateBunkerHours / maxFuel are used
 * @param {Port}   port     the stop's port (size, services.fuel, optional canHandleCargo)
 * @param {Array}  actions  the stop's `actions` ([{ type, cargoType, quantity, conditions }])
 * @param {object} sim      MUTATED in place so callers can chain stops:
 *   { lots: [{ type, quantity, contractId?, destinationPort? }], fuel }
 *   `fuel` is in the same unit as ship.maxFuel (tonnes). Pass copies if you need the original.
 * @param {object} [opts]
 * @param {object} [opts.manager]        { canPerformAction(type) }; absent = nothing skipped
 * @param {object} [opts.portStock]      { cargoType: TEU } buyable here; absent = unknown, assume plenty
 * @param {object|null} [opts.warehouse] this port's warehouse { cargo: [{type, quantity}], freeCapacity };
 *                                       null = none here, undefined = unknown (assume one with room)
 * @param {Array}  [opts.interestList]   route interest list; when given and empty, interest_buy fails
 * @param {Array}  [opts.contractPickups] contract lots loaded on arrival at this port:
 *                                       [{ type, quantity, contractId, destinationPort }]
 * @param {number} [opts.capacity]       override for ship.getCargoCapacity()
 * @returns {{ hours:number, handlingTeu:number, bunkerTonnes:number,
 *             flags:string[], skippedActions:Array<{index:number,type:string,reason:string}> }}
 */
export function estimateStopPortHours(ship, port, actions, sim, opts = {}) {
  const flags = new Set();
  const skippedActions = [];
  const lots = sim.lots || (sim.lots = []);
  if (!(sim.fuel >= 0)) sim.fuel = ship?.fuel || 0;

  const capacity = opts.capacity ?? (ship?.getCargoCapacity ? ship.getCargoCapacity() : 0);
  const free = () => capacity - lotTeu(lots);

  let handlingTeu = 0;
  let bunkerTonnes = 0;

  const skip = (index, action, reason) => skippedActions.push({ index, type: action.type, reason });
  const portCanHandle = type => type === ASSUMED_TYPE || !port?.canHandleCargo || port.canHandleCargo(type);
  const stockOf = type => (opts.portStock ? (opts.portStock[type] || 0) : Infinity);

  // 1. Contract deliveries for this port unload first (ContractSystem listens before the route acts)
  for (let i = lots.length - 1; i >= 0; i--) {
    const l = lots[i];
    if (l.contractId && l.destinationPort === port?.id) {
      handlingTeu += l.quantity || 0;
      lots.splice(i, 1);
    }
  }
  // ...and contract pickups at this port load (when they fit)
  for (const p of (opts.contractPickups || [])) {
    if (free() >= p.quantity) {
      lots.push({ type: p.type, quantity: p.quantity, contractId: p.contractId, destinationPort: p.destinationPort });
      handlingTeu += p.quantity;
    }
  }

  const sellLot = i => { handlingTeu += lots[i].quantity || 0; lots.splice(i, 1); };

  // 2. Route actions, in order
  (actions || []).forEach((action, index) => {
    const type = action.type;
    if (opts.manager && !opts.manager.canPerformAction(type)) {
      skip(index, action, 'manager-tier');
      return;
    }
    const c = action.conditions || {};
    if (c.maxPrice || c.minPrice || c.minProfitPerTEU) flags.add('conditional');

    switch (type) {
      case 'wait':
        return;

      case 'buy': {
        let qty = isFill(action.quantity) ? free() : Number(action.quantity);
        if (c.maxQuantity) qty = Math.min(qty, c.maxQuantity);
        if (!(qty > 0)) return skip(index, action, 'no-capacity');
        if (qty > free()) return skip(index, action, 'no-capacity');
        if (!portCanHandle(action.cargoType)) return skip(index, action, 'no-terminal');
        const stock = stockOf(action.cargoType);
        if (qty > stock) { flags.add('stock-limited'); return skip(index, action, 'stock'); }
        lots.push({ type: action.cargoType, quantity: qty });
        handlingTeu += qty;
        return;
      }

      case 'smart_buy':
      case 'interest_buy': {
        flags.add('smart-action');
        if (type === 'interest_buy' && opts.interestList && opts.interestList.length === 0) {
          return skip(index, action, 'no-interest-list');
        }
        let qty = free();
        if (opts.portStock) {
          const total = Object.values(opts.portStock).reduce((s, n) => s + (n || 0), 0);
          if (total < qty) flags.add('stock-limited');
          qty = Math.min(qty, total);
        }
        if (!(qty > 0)) return skip(index, action, 'no-capacity-or-stock');
        lots.push({ type: ASSUMED_TYPE, quantity: qty });
        handlingTeu += qty;
        return;
      }

      case 'sell': {
        const i = lots.findIndex(l => l.type === action.cargoType || (l.type === ASSUMED_TYPE && action.cargoType));
        if (i === -1) return skip(index, action, 'not-in-hold');
        if (!portCanHandle(lots[i].type)) return skip(index, action, 'no-terminal');
        sellLot(i);
        return;
      }

      case 'sell_all':
      case 'smart_sell': {
        if (type === 'smart_sell') flags.add('smart-action');
        let any = false;
        for (let i = lots.length - 1; i >= 0; i--) {
          const l = lots[i];
          if (type === 'sell_all' && action.cargoType && l.type !== action.cargoType && l.type !== ASSUMED_TYPE) continue;
          if (!portCanHandle(l.type)) continue;
          sellLot(i);
          any = true;
        }
        if (!any) skip(index, action, 'not-in-hold');
        return;
      }

      case 'warehouse_unload': {
        if (opts.warehouse === null) return skip(index, action, 'no-warehouse');
        flags.add('warehouse-stock');
        let room = opts.warehouse ? opts.warehouse.freeCapacity : Infinity;
        let any = false;
        const all = action.cargoType === '__all__';
        // __all__ walks every lot (from the end, like the real system); a single type
        // touches only the FIRST matching lot (RouteAutomationSystem uses findIndex).
        const order = all ? lots.map((_, k) => k).reverse() : [lots.findIndex(l => l.type === action.cargoType)];
        for (const i of order) {
          if (i < 0) break;
          const l = lots[i];
          const want = (all || action.quantity === 'all') ? l.quantity : Math.min(Number(action.quantity) || 0, l.quantity);
          const moved = Math.min(want, room);
          if (!(moved > 0)) continue;
          room -= moved;
          handlingTeu += moved;
          l.quantity -= moved;
          if (l.quantity <= 0) lots.splice(i, 1);
          any = true;
        }
        if (!any) skip(index, action, 'nothing-to-unload');
        return;
      }

      case 'warehouse_load': {
        if (opts.warehouse === null) return skip(index, action, 'no-warehouse');
        flags.add('warehouse-stock');
        let avail = Infinity;
        if (opts.warehouse) {
          const lot = (opts.warehouse.cargo || []).find(w => w.type === action.cargoType);
          if (!lot) return skip(index, action, 'not-in-warehouse');
          avail = lot.quantity;
        }
        const want = isFill(action.quantity) ? Infinity : Number(action.quantity);
        const qty = Math.min(want, avail, free());
        if (!(qty > 0) || !isFinite(qty)) return skip(index, action, 'no-capacity-or-stock');
        lots.push({ type: action.cargoType, quantity: qty });
        handlingTeu += qty;
        return;
      }

      case 'refuel':
      case 'smart_refuel': {
        if (type === 'smart_refuel') flags.add('smart-action');
        if (port?.services && !port.services.fuel) return skip(index, action, 'no-fuel-service');
        const maxFuel = ship?.maxFuel || 0;
        if (c.fuelBelowPercent && maxFuel > 0 && (sim.fuel / maxFuel) * 100 >= c.fuelBelowPercent) {
          return skip(index, action, 'fuel-above-threshold');
        }
        const added = Math.max(0, maxFuel - sim.fuel);
        if (!(added > 0)) return skip(index, action, 'tank-full');
        sim.fuel = maxFuel;
        bunkerTonnes += added;
        return;
      }

      default:
        return skip(index, action, 'unknown-action');
    }
  });

  // 3. Port time: one handling session (setup once) alongside one bunkering session
  const handling = handlingTeu > 0 && ship?.estimateHandlingHours
    ? ship.estimateHandlingHours(port, handlingTeu, true) : 0;
  const bunkering = bunkerTonnes > 0 && ship?.estimateBunkerHours
    ? ship.estimateBunkerHours(port, bunkerTonnes, true) : 0;

  return {
    hours: Math.max(handling, bunkering),
    handlingTeu,
    bunkerTonnes,
    flags: [...flags],
    skippedActions
  };
}
